import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoArrowBack,
  IoReorderThreeOutline,
  IoArrowUp,
  IoArrowDown,
  IoRefreshOutline,
} from "react-icons/io5";
import styles from "./CreatorOrder.module.css";
import { getCreatorDisplayOrder, saveCreatorDisplayOrder } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";

// Experience Builder > Creators tab.
//
// Controls the order creators appear in on the public /creators directory and
// the creator rails. The sort happens server-side (users.display_order), so
// every client picks it up -- web and the TV apps (Roku/Samsung/Fire TV/Apple)
// alike -- with no app release needed.
//
// Deliberately kept out of HomePageBuilder.js: that file is a page/section
// builder with its own draft/publish, version-history and undo/redo machinery,
// and creators are not SectionCategory content. This shares the builder's
// navigation, not its code.
//
// The list is loaded unpaginated on purpose. The public directory pages 24 at
// a time, but you cannot drag a creator onto a page that isn't rendered, so
// "move this one to last" is only expressible when every creator is on screen.
export default function CreatorOrder() {
  const navigate = useNavigate();
  const { can } = useRbac();
  const canEdit = can("categories", "edit");

  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The order as last known to the server. Compared against `creators` to
  // decide whether there is anything to save, and used to revert.
  const [savedOrder, setSavedOrder] = useState([]);

  const dragIndexRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const fetchCreators = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCreatorDisplayOrder();
      const rows = response?.data?.data || [];
      setCreators(rows);
      setSavedOrder(rows.map((c) => c.id));
    } catch (err) {
      showToast(err?.response?.data?.message || "Error loading creators", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  const isDirty = useMemo(() => {
    if (creators.length !== savedOrder.length) return true;
    return creators.some((c, i) => c.id !== savedOrder[i]);
  }, [creators, savedOrder]);

  // Warn before losing an unsaved arrangement to a tab close / refresh.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const move = useCallback(
    (from, to) => {
      if (!canEdit) {
        showToast("You don't have permission to reorder creators", "error");
        return;
      }
      if (from === to || from == null || to == null) return;
      setCreators((prev) => {
        if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
        const next = [...prev];
        const [row] = next.splice(from, 1);
        next.splice(to, 0, row);
        return next;
      });
    },
    [canEdit]
  );

  // Drag-and-drop uses native HTML5 events, matching the Experience Builder's
  // existing pattern -- AdminPanel has no drag-and-drop library and this
  // doesn't justify adding one.
  const handleDragStart = (index) => (e) => {
    if (!canEdit) return;
    dragIndexRef.current = index;
    setDragIndex(index);
    // Firefox won't start a drag without data on the transfer object.
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(index));
    } catch {
      /* some browsers disallow setData during dragstart -- harmless */
    }
  };

  const handleDragOver = (index) => (e) => {
    if (!canEdit || dragIndexRef.current === null) return;
    e.preventDefault(); // required or the drop never fires
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (index) => (e) => {
    if (!canEdit) return;
    e.preventDefault();
    move(dragIndexRef.current, index);
    dragIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    const ids = creators.map((c) => c.id);
    try {
      await saveCreatorDisplayOrder(ids);
      showToast("Creator order saved", "success");
      // Awaited, and savedOrder is derived from what comes BACK rather than
      // from what we sent: if another admin saved in the meantime, or the
      // refetch fails, we must not leave the screen looking clean over an
      // order the server doesn't actually hold.
      await fetchCreators();
    } catch (err) {
      showToast(err?.response?.data?.message || "Error saving creator order", "error");
    } finally {
      setSaving(false);
    }
  };

  // Re-read rather than rebuilding locally: mapping savedOrder through the
  // current rows and dropping misses (.filter(Boolean)) would silently shrink
  // the list whenever the two diverged, which is the opposite of "revert".
  const handleRevert = () => {
    fetchCreators();
  };

  const renderRow = (creator, index) => {
    const isDragging = dragIndex === index;
    const isDragOver = dragOverIndex === index && dragIndex !== index;
    const rowClass = [
      styles.row,
      isDragging ? styles.rowDragging : "",
      isDragOver ? styles.rowDragOver : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <li
        key={creator.id}
        className={rowClass}
        draggable={canEdit}
        onDragStart={handleDragStart(index)}
        onDragOver={handleDragOver(index)}
        onDrop={handleDrop(index)}
        onDragEnd={handleDragEnd}
        onDragLeave={() => setDragOverIndex((cur) => (cur === index ? null : cur))}
      >
        <span className={styles.position}>{index + 1}</span>

        <span className={styles.handle} title={canEdit ? "Drag to reorder" : undefined}>
          <IoReorderThreeOutline />
        </span>

        {creator.profile_img ? (
          <img
            className={styles.avatar}
            src={creator.profile_img}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : (
          <span className={styles.avatarFallback} aria-hidden="true">
            {(creator.name || "?").charAt(0).toUpperCase()}
          </span>
        )}

        <span className={styles.meta}>
          <span className={styles.name}>{creator.name || "Untitled creator"}</span>
          {creator.channel_name && (
            <span className={styles.channel}>{creator.channel_name}</span>
          )}
        </span>

        {/* "Put us last" is the request admins actually make -- one click here
            beats dragging a row past dozens of others. */}
        <span className={styles.rowActions}>
          <button
            type="button"
            className={styles.moveButton}
            disabled={!canEdit || index === 0}
            onClick={() => move(index, 0)}
            title="Move to top"
            aria-label={`Move ${creator.name || "creator"} to top`}
          >
            <IoArrowUp />
          </button>
          <button
            type="button"
            className={styles.moveButton}
            disabled={!canEdit || index === creators.length - 1}
            onClick={() => move(index, creators.length - 1)}
            title="Move to bottom"
            aria-label={`Move ${creator.name || "creator"} to bottom`}
          >
            <IoArrowDown />
          </button>
        </span>
      </li>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/mainlayout/experience-builder")}
        >
          <IoArrowBack /> Experience Builder
        </button>
      </div>

      <h1 className={styles.title}>Creator Order</h1>
      <p className={styles.subtitle}>
        Arrange how creators appear on your website&apos;s Creators page. Drag a
        row, or use the arrows to send one straight to the top or bottom. The
        order applies everywhere creators are listed, including your TV apps.
      </p>

      {!canEdit && (
        <div className={styles.readOnlyNotice}>
          You have view-only access to the Experience Builder, so this order
          can&apos;t be changed.
        </div>
      )}

      <div className={styles.toolbar}>
        <span className={styles.count}>
          {loading ? "Loading…" : `${creators.length} creator${creators.length === 1 ? "" : "s"}`}
        </span>
        <span className={styles.toolbarRight}>
          {isDirty && !loading && (
            <span className={styles.unsaved}>Unsaved changes</span>
          )}
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={handleRevert}
            disabled={!isDirty || saving}
          >
            <IoRefreshOutline /> Revert
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSave}
            disabled={!canEdit || !isDirty || saving}
          >
            {saving ? "Saving…" : "Save order"}
          </button>
        </span>
      </div>

      {loading ? (
        <div className={styles.emptyState}>Loading creators…</div>
      ) : creators.length === 0 ? (
        <div className={styles.emptyState}>
          No approved creators yet. Once creators are approved they&apos;ll show
          up here and you can arrange their order.
        </div>
      ) : (
        <ol className={styles.list}>{creators.map(renderRow)}</ol>
      )}
    </div>
  );
}
